import { AgTechButton, AgTechClickEventHandler, AgTechButtonProps } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechClickableIcon } from '@agtech/web/components/Icons/AgTechIcons';
import AgTechBorderedList from '@agtech/web/components/Lists/AgTechBorderedList';
import AgTechNoRecordsPage from '@agtech/web/components/Pages/Error/AgTechNoRecordsPage';
import React, { useRef } from 'react'
import AddPaymentDialog from './payments/InvoiceAddPaymentDialog';
import InvoiceChangeContactCreditsDialog from './changes/InvoiceChangeContactCreditsDialog';
import InvoiceChangeInvoiceModifiersDialog from './changes/InvoiceChangeInvoiceModifiersDialog';
import InvoiceChangeLotsDialog from './changes/InvoiceChangeLotsDialog';
import { SaleInvoiceModifier } from '@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierData';
import { SaleLot } from '@sales/data/lots/LotData';
import { ContactCredit } from '@sales/data/operation/contacts/credits/ContactCreditData';
import { useAgTechQuery, useAgTechQueryResultCombination } from '@agtech/core/data/queries/AgTechDataQueries';
import { InvoiceDataGetInvoiceDocumentQuery, useInvoiceDataGetInvoiceDetailsQuery } from '@sales/data/invoicing/InvoicingDataQueries';
import { getInvoiceFileName, InvoiceDetails, InvoiceStatus } from '@sales/data/invoicing/InvoicingData';
import AgTechTextArea from '@agtech/web/components/Text/AgTechTextArea';
import SaleContactDetailDialog from '../../contacts/dialogs/SaleContactDetailDialog';
import AgTechPdfViewer, { AgTechPdfHandler } from '@agtech/web/components/Widgets/AgTechPdfViewer';
import AgTechViewDialog from '@agtech/web/components/Portals/Dialogs/AgTechViewDialog';
import AgTechActionPane from '@agtech/web/components/Headers/AgTechActionPane';
import { getContactFullName } from '@agtech/data/contacts/ContactData';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import { AgTechFormReadOnlyTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl';
import dayjs from 'dayjs';
import ViewPaymentsDialog from './payments/InvoiceViewPaymentsDialog';

export type InvoiceInfo = {
    id: number,
    invoiceNumber?: string,
    invoiceTotal: number,
    selectedLots: SaleLot[],
    appliedInvoiceModifiers: SaleInvoiceModifier[],
    appliedCredits: ContactCredit[],
    purchaserName: string,
    invoiceDate: Date,
    contactId: number
}

export type InvoiceInfoDialogProps = {
    invoiceId: number,
    invoice?: InvoiceInfo | undefined,
    invoiceDocument?: Blob | undefined,
    actions?: {
        close?: () => void,
    },
    showNextSteps: boolean,
    growAfterLoading?: boolean
}

const InvoiceInfoDialog = (props: InvoiceInfoDialogProps) => {
    const dialogQuery = useInvoiceInfoDialogQuery(props);
    const pdfRef = useRef<AgTechPdfHandler>(null);

    return (
        <AgTechViewDialog
            data={{
                query: dialogQuery
            }}
            settings={{
                growAfterLoading: props.growAfterLoading
            }}
            structure={{
                width: '1000px',
                header: (data) => ({
                    header: `Invoice #${data.invoiceDetails.invoice.info.number}`,
                    settings: {
                        classes: 'mb-3 px-5 py-3 border-b',
                        header: {
                            classes: '!text-md'
                        },
                        content: {
                            body: () => (
                                <AgTechActionPane
                                    elements={[
                                        <AgTechButton
                                            icon='fas fa-print'
                                            text='Print'
                                            action={() => {
                                                if (pdfRef?.current) {
                                                    pdfRef.current.print();
                                                }
                                            }}
                                        />,
                                        <AgTechButton
                                            icon='fas fa-download'
                                            text='Download'
                                            action={() => {
                                                if (pdfRef?.current) {
                                                    const invoicePdfFileName = getInvoiceFileName({
                                                        invoiceContact: data.invoiceDetails.contact.info,
                                                        invoiceNumber: data.invoiceDetails.invoice.info.number,
                                                        invoiceDate: data.invoiceDetails.invoice.info.date
                                                    });
                                                    
                                                    pdfRef.current.download(invoicePdfFileName);
                                                }
                                            }}
                                        />
                                    ]}
                                />
                            )
                        }
                    }
                })
            }}
        >
            {(dialogData) => (
                <InvoiceInfoDialogBody
                    ref={pdfRef}
                    invoiceDetails={dialogData.invoiceDetails}
                    invoiceDocument={dialogData.document}
                    actions={props.actions}
                    showNextSteps={props.showNextSteps}
                />
            )}
        </AgTechViewDialog>
    )
}

export type InvoiceInfoDialogBodyProps = {
    invoiceDetails: InvoiceDetails,
    invoiceDocument: Blob,
    actions?: {
        close?: () => void
    },
    showNextSteps: boolean
}

const InvoiceInfoDialogBody = React.forwardRef<AgTechPdfHandler, InvoiceInfoDialogBodyProps>((props, ref) => {
    if (!props.invoiceDetails || !props.invoiceDocument) {
        return (
            <AgTechNoRecordsPage
                message='Unable to find the invoice. Please try again'
            />
        )
    }

    const invoiceFile: File | undefined = props.invoiceDocument
        ? new File([props.invoiceDocument], props.invoiceDetails.invoice.id.toString(), { type: 'application/pdf' })
        : undefined;

    const invoiceId = props.invoiceDetails.invoice.id;
    const invoiceDetails = props.invoiceDetails.invoice.details;
    const invoiceContact = props.invoiceDetails.contact.info;
    
    return (
        <div className='column-based stretched non-scrollable flex'>
            <div className='flexed row-based tall border-r flex'>
                <div className='row-based flexed bg-white flex'>
                    <div className='row-based flexed p-5 pt-2 flex'>
                        <AgTechFormRow classes='pb-6'>
                            <AgTechFormReadOnlyTextControl
                                entityType={props.invoiceDetails.invoice}
                                caption='Invoice Date'
                                value={() => dayjs(props.invoiceDetails.invoice.info.date).format('MM/DD/YYYY')}
                                classes='flexed'
                            />
                            <AgTechFormReadOnlyTextControl
                                entityType={props.invoiceDetails.invoice}
                                caption='Status'
                                value={() => InvoiceStatus[props.invoiceDetails.invoice.info.status]}
                                classes='flexed'
                            />
                        </AgTechFormRow>
                        <AgTechTextArea
                            header={{
                                caption: 'Contact',
                                body: () => (
                                    <SaleContactDetailDialog.Button
                                        contactId={invoiceContact.id}
                                        classes='button-link ml-auto pr-1 text-sm'
                                    />
                                )
                            }}
                            text={`${getContactFullName(invoiceContact)} ${invoiceContact.ranchName ? ' - ' + invoiceContact.ranchName : ''}\n${invoiceContact.emailAddress}${invoiceContact.phoneNumber ? ' | ' + invoiceContact.phoneNumber : ''}\n\n${invoiceContact.address}\n${invoiceContact.city}, ${invoiceContact.stateAbbreviation} ${invoiceContact.zip}`}
                            rows={5}
                        />
                        <div className='row-based py-6 flex'>
                            <h1 className='font-semibold text-sm mb-2 ml-half'>Details</h1>
                            <AgTechBorderedList
                                items={[
                                    () => (
                                        <div className='wide column-based-vertically-centered flex'>
                                            <h1 className='text-sm font-semibold text-detail ml-3'>Lots Invoiced</h1>
                                            <h1 className='text-sm font-semibold ml-auto'>{invoiceDetails.lotsPurchased.length}</h1>
                                            <AgTechClickableIcon
                                                icon='bi bi-pencil-square'
                                                classes='px-3 py-2 ml-3 border-l'
                                                onClick={e => {
                                                    InvoiceChangeLotsDialog.Open(e, {
                                                        invoiceId: invoiceId,
                                                        contactId: invoiceContact.id
                                                    });
                                                }}
                                                tooltip='Update invoice lots'
                                            />
                                        </div>
                                    ),
                                    () => (
                                        <div className='wide column-based-vertically-centered flex'>
                                            <h1 className='text-sm text-detail font-semibold ml-3'>Charges and Discounts Applied</h1>
                                            <h1 className='text-sm font-semibold ml-auto'>{invoiceDetails.InvoiceModifiersGiven.length}</h1>
                                            <AgTechClickableIcon
                                                icon='bi bi-pencil-square'
                                                classes='px-3 py-2 ml-3 border-l'
                                                onClick={e => {
                                                    InvoiceChangeInvoiceModifiersDialog.Open(e, {
                                                        invoiceId: invoiceId,
                                                        contactId: invoiceContact.id
                                                    })
                                                }}
                                                tooltip='Update invoice charges and discounts'
                                            />
                                        </div>
                                    ),
                                    () => (
                                        <div className='wide column-based-vertically-centered flex'>
                                            <h1 className='text-sm font-semibold text-detail ml-3'>Credits Given</h1>
                                            <h1 className='text-sm font-semibold ml-auto'>{invoiceDetails.creditsApplied?.length ?? 0}</h1>
                                            <AgTechClickableIcon
                                                icon='bi bi-pencil-square'
                                                classes='px-3 py-2 ml-3 border-l'
                                                onClick={e => {
                                                    InvoiceChangeContactCreditsDialog.Open(e, {
                                                        invoiceId: invoiceId,
                                                        contactId: invoiceContact.id
                                                    })
                                                }}
                                                tooltip='Update invoice credits'
                                            />
                                        </div>
                                    ),
                                    () => (
                                        <div className='wide column-based-vertically-centered flex'>
                                            <h1 className='text-sm font-semibold text-detail ml-3'>Payments Made</h1>
                                            <h1 className='text-sm font-semibold ml-auto'>{invoiceDetails.paymentsMade?.length ?? 0}</h1>
                                            <AgTechClickableIcon
                                                icon='bi bi-pencil-square'
                                                classes='px-3 py-2 ml-3 border-l'
                                                onClick={e => {
                                                    if (invoiceDetails.paymentsMade.length > 0) {
                                                        ViewPaymentsDialog.Open(e, { invoiceId: invoiceId });
                                                    }
                                                    else {
                                                        AddPaymentDialog.Open(e, { invoiceId: invoiceId });
                                                    }
                                                }}
                                                tooltip={'Update invoice payments'}
                                            />
                                        </div>
                                    )
                                ]}
                            />
                        </div>
                        {props.showNextSteps &&
                            <div className='row-based wide mt-auto flex'>
                                {props.invoiceDetails.invoice.info.status !== InvoiceStatus.Paid && (
                                    <AddPaymentDialog.Button
                                        invoiceId={invoiceId}
                                        classes='button-highlight button-block'
                                    />
                                )}
                            </div>
                        }
                    </div>
                </div>
            </div>
            <div className='row-based tall centered p-5 bg-secondary flex' style={{ width: 600 }}>
                <AgTechPdfViewer
                    ref={ref}
                    file={invoiceFile}
                />
            </div>
        </div>
    );
});

export const useInvoiceInfoDialogQuery = (props: InvoiceInfoDialogProps) => {
    const invoiceDetailsQuery = useInvoiceDataGetInvoiceDetailsQuery({
        invoiceId: props.invoiceId
    });

    const invoiceDocumentQuery = useAgTechQuery({
        query: InvoiceDataGetInvoiceDocumentQuery,
        queryContext: { invoiceId: props.invoiceId }
    });

    return useAgTechQueryResultCombination({
        firstQuery: invoiceDetailsQuery,
        secondQuery: invoiceDocumentQuery,
        projection: (details, document) => ({
            invoiceDetails: details,
            document
        })
    });
}

InvoiceInfoDialog.Open = (e: AgTechClickEventHandler, props: InvoiceInfoDialogProps) => e.dialogs.openDialog({
    dialog: () => <InvoiceInfoDialog {...props} />
});

InvoiceInfoDialog.Button = (props: InvoiceInfoDialogProps & AgTechButtonProps) => {
    return (
        <AgTechButton
            text='View'
            icon='fas fa-eye'
            {...props}
            action={e => InvoiceInfoDialog.Open(e, props)}
        />
    )
}

export default InvoiceInfoDialog